import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatNumber, formatRate, type ConversionGoal, type ConversionReport } from "@/lib/comms";

const MIN_HOLDOUT_HINT = "needs 20+ holdouts";

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "good" | "muted" }) {
  const valueClass = tone === "good" ? "text-emerald-600 dark:text-emerald-400" : tone === "muted" ? "text-muted-foreground" : "";
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
      {sub ? <p className="text-xs text-muted-foreground leading-snug">{sub}</p> : null}
    </div>
  );
}

/**
 * The three numbers that say whether a message did anything: what share of the people who got
 * it converted, what share of the people deliberately kept back converted anyway, and the gap
 * between the two. Only the gap is evidence.
 */
export default function ConversionPanel({
  report,
  goals,
  title = "Conversions",
}: {
  report: ConversionReport;
  goals?: ConversionGoal[];
  title?: string;
}) {
  const goalLabel = goals?.find(g => g.id === report.goal)?.label || report.goal;
  const noGoal = !report.goal || report.goal === "none";

  if (noGoal) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground" data-testid="conversion-no-goal">
        No conversion goal is set, so there is nothing to measure. Pick one when you edit this, and attribution starts from the next send.
      </div>
    );
  }

  const uplift = report.upliftPoints;

  return (
    <div className="space-y-3" data-testid="conversion-report">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium">{title}</p>
        <Badge variant="outline">{goalLabel}</Badge>
        <span className="text-xs text-muted-foreground">within {report.windowDays} day{report.windowDays === 1 ? "" : "s"} of the send</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile
          label="Converted"
          value={`${formatNumber(report.converted)} · ${formatRate(report.rate)}`}
          sub={`of ${formatNumber(report.sent)} sent`}
        />
        <Tile
          label="Holdout"
          value={`${formatNumber(report.holdout)} · ${formatRate(report.holdoutRate)}`}
          sub={`${formatNumber(report.holdoutConverted)} of them converted without being messaged`}
        />
        <Tile
          label="Uplift"
          value={uplift === null || uplift === undefined ? MIN_HOLDOUT_HINT : `${uplift >= 0 ? "+" : ""}${uplift.toFixed(2)} pts`}
          sub={
            uplift === null || uplift === undefined
              ? "Too small a holdout to tell the difference from noise"
              : "Conversion rate minus the holdout's rate"
          }
          tone={uplift === null || uplift === undefined ? "muted" : uplift > 0 ? "good" : undefined}
        />
      </div>

      {report.variants && report.variants.length > 0 && (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Variant</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Opened</TableHead>
                <TableHead className="text-right">Clicked</TableHead>
                <TableHead className="text-right">Converted</TableHead>
                <TableHead className="text-right">Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.variants.map(v => (
                <TableRow key={v.variant} data-testid={`row-variant-${v.variant}`}>
                  <TableCell className="font-medium">{v.variant}</TableCell>
                  <TableCell className="text-right">{formatNumber(v.sent)}</TableCell>
                  <TableCell className="text-right">{formatNumber(v.opened)}</TableCell>
                  <TableCell className="text-right">{formatNumber(v.clicked)}</TableCell>
                  <TableCell className="text-right">{formatNumber(v.converted)}</TableCell>
                  <TableCell className="text-right font-medium">{formatRate(v.rate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
